/**
 * /university/training — list of training sessions, filterable by status.
 */
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ChevronLeft, Loader2 } from 'lucide-react';
import { z } from 'zod';

import { SessionCard } from '../../../../components/university/session-card';
import {
  type ListTrainingSessionsParams,
  learningApi,
  learningKeys,
} from '../../../../lib/api/learning';
import { cn } from '../../../../lib/utils';

const trainingSearchSchema = z.object({
  status: z
    .enum(['all', 'planned', 'active', 'awaiting_tuner', 'completed', 'abandoned'])
    .optional(),
});

function TrainingIndexRoute() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const status = (search.status ?? 'all') as
    | 'all'
    | NonNullable<ListTrainingSessionsParams['status']>;

  const params: ListTrainingSessionsParams = status === 'all' ? {} : { status };

  const sessionsQ = useQuery({
    queryKey: learningKeys.trainingSessionsList(params),
    queryFn: () => learningApi.listTrainingSessions(params),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });

  const sessions = sessionsQ.data?.sessions ?? [];

  return (
    <section className="mx-auto flex w-full max-w-4xl flex-col gap-4 p-6">
      <header className="flex flex-col gap-1">
        <Link
          to="/university"
          className="inline-flex w-fit items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-3 w-3" aria-hidden /> Back to University
        </Link>
        <div className="flex items-center justify-between">
          <h1 className="text-[16px] font-semibold tracking-tight text-foreground">
            Training sessions
            <span className="ml-2 text-[12px] font-normal text-muted-foreground">
              {sessions.length} {sessions.length === 1 ? 'session' : 'sessions'}
            </span>
          </h1>
          {sessionsQ.isFetching ? (
            <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> syncing…
            </span>
          ) : null}
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-1">
        {(
          [
            { value: 'all', label: 'All' },
            { value: 'planned', label: 'Planned' },
            { value: 'active', label: 'Active' },
            { value: 'completed', label: 'Completed' },
            { value: 'abandoned', label: 'Abandoned' },
          ] as const
        ).map((opt) => (
          <FilterChip
            key={opt.value}
            label={opt.label}
            active={status === opt.value}
            onClick={() =>
              navigate({
                search: (prev) => ({
                  ...prev,
                  status: opt.value === 'all' ? undefined : opt.value,
                }),
                replace: true,
              })
            }
          />
        ))}
      </div>

      {sessionsQ.isPending ? (
        <div className="px-2 py-4 text-[12px] text-muted-foreground">Loading sessions…</div>
      ) : sessionsQ.isError ? (
        <div className="rounded border border-destructive/50 bg-destructive/5 p-3 text-[12px] text-destructive">
          Failed to load sessions: {(sessionsQ.error as Error).message}
        </div>
      ) : sessions.length === 0 ? (
        <div className="px-2 py-6 text-center text-[12px] text-muted-foreground/70">
          No training sessions match the current filter.
        </div>
      ) : (
        <ol className="flex flex-col gap-2">
          {sessions.map((session) => (
            <li key={session.id}>
              <SessionCard session={session} />
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

interface FilterChipProps {
  label: string;
  active: boolean;
  onClick: () => void;
}

function FilterChip({ label, active, onClick }: FilterChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded px-2 py-0.5 text-[11px] transition-colors',
        active
          ? 'bg-foreground/8 text-foreground'
          : 'text-muted-foreground hover:bg-foreground/5 hover:text-foreground',
      )}
    >
      {label}
    </button>
  );
}

export const Route = createFileRoute('/_authenticated/university/training/')({
  validateSearch: trainingSearchSchema,
  component: TrainingIndexRoute,
});
